import React from 'react';
import { Anime, AnimeSeason, SEASON_CONFIGS } from '../types/anime';
import { isAiringToday } from '../utils/helpers';
import { Tv, Sparkles, Bell, HelpCircle, RotateCcw } from 'lucide-react';
import { FilterSelect } from './FilterSelect';
import { AIRING_FILTER_LABELS, AiringFilter } from '../utils/airingStatus';

export type AudioFilter = 'ALL' | 'ซับไทย' | 'พากย์ไทย';

/** All list filters at once (search excluded) */
export interface FilterValues {
  season: AnimeSeason | 'ALL';
  year: number | 'ALL';
  airing: AiringFilter;
  audio: AudioFilter;
}

interface SeasonHeroBarProps {
  selectedSeason: AnimeSeason | 'ALL';
  onSelectSeason: (season: AnimeSeason | 'ALL') => void;
  selectedYear: number | 'ALL';
  onSelectYear: (year: number | 'ALL') => void;
  airingFilter: AiringFilter;
  onSelectAiringFilter: (filter: AiringFilter) => void;
  selectedAudio: 'ALL' | 'ซับไทย' | 'พากย์ไทย';
  onSelectAudio: (audio: 'ALL' | 'ซับไทย' | 'พากย์ไทย') => void;
  animeList: Anime[];
  filteredList: Anime[];
  /** How many shows would show if one filter changed and the rest stayed as they are */
  countWith: (override: Partial<FilterValues>) => number;
  /** Back to the default filters */
  onResetFilters: () => void;
  onSwitchToSchedule?: () => void;
}

export const SeasonHeroBar: React.FC<SeasonHeroBarProps> = ({
  selectedSeason,
  onSelectSeason,
  selectedYear,
  onSelectYear,
  airingFilter,
  onSelectAiringFilter,
  selectedAudio,
  onSelectAudio,
  animeList,
  filteredList,
  countWith,
  onResetFilters,
  onSwitchToSchedule,
}) => {
  const seasonsOrder: AnimeSeason[] = ['WINTER', 'SPRING', 'SUMMER', 'FALL'];
  const availableYears = Array.from(
    new Set([new Date().getFullYear(), 2026, 2025, 2024, ...animeList.map((a) => a.year || 2026)])
  ).sort((a, b) => b - a);

  // Every count respects all the other active filters, so a number always matches what tapping it shows
  const countBySeason = (s: AnimeSeason | 'ALL') => countWith({ season: s });
  const isDefault =
    selectedSeason === 'ALL' &&
    selectedYear === new Date().getFullYear() &&
    airingFilter === 'AIRING_RECENT' &&
    selectedAudio === 'ALL';

  const airingTodayCount = filteredList.filter((a) => isAiringToday(a)).length;
  const unwatchedCount = filteredList.filter(
    (a) => (Number(a.latestEp) || 0) > (Number(a.watchedEp) || 0)
  ).length;
  const unknownDayCount = filteredList.filter(
    (a) => a.broadcastDay === 'UNKNOWN' && a.status !== 'จบแล้ว'
  ).length;
  const lineEnabledCount = filteredList.filter((a) => a.notifyLine).length;

  const stats = [
    { label: 'ฉายวันนี้', short: 'วันนี้', value: airingTodayCount, icon: Tv, tone: 'text-rose-300 bg-rose-500/10', onClick: onSwitchToSchedule },
    { label: 'ยังไม่ได้ดู', short: 'ยังไม่ดู', value: unwatchedCount, icon: Sparkles, tone: 'text-amber-300 bg-amber-500/10' },
    { label: 'ไม่ทราบวันฉาย', short: 'ไม่รู้วัน', value: unknownDayCount, icon: HelpCircle, tone: 'text-teal-300 bg-teal-500/10', onClick: onSwitchToSchedule },
    { label: 'แจ้งเตือน LINE', short: 'LINE', value: lineEnabledCount, icon: Bell, tone: 'text-[#4ef08f] bg-[#06C755]/10' },
  ];

  // Phones: five slim equal tiles in one row (name over count). sm+: the scrolling pill row
  const chip = (active: boolean) =>
    `min-w-0 h-11 px-1 rounded-xl flex flex-col items-center justify-center gap-0.5 sm:shrink-0 sm:h-9 sm:pl-3 sm:pr-2 sm:rounded-full sm:flex-row sm:gap-1.5 text-[10.5px] sm:text-[13px] font-medium border transition-all active:scale-95 ${
      active
        ? 'bg-white text-slate-900 border-white shadow-[0_6px_20px_-6px_rgba(255,255,255,0.35)]'
        : 'bg-white/[0.03] text-slate-300 border-white/[0.07] hover:bg-white/[0.07] hover:text-white'
    }`;
  const chipCount = (active: boolean) =>
    `sm:min-w-[22px] sm:h-[22px] sm:px-1.5 sm:rounded-full grid place-items-center text-[11px] font-bold sm:font-semibold leading-none ${
      active ? 'text-slate-700 sm:bg-slate-900/10' : 'text-slate-400 sm:bg-white/[0.06]'
    }`;

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 pt-3 sm:pt-6 space-y-2 sm:space-y-3">
      {/* Season chips + filters */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 sm:gap-3">
        <div className="grid grid-cols-5 gap-1.5 sm:flex sm:gap-2 sm:overflow-x-auto no-scrollbar flex-1">
          <button type="button" onClick={() => onSelectSeason('ALL')} className={chip(selectedSeason === 'ALL')}>
            <span className="whitespace-nowrap">
              <span className="sm:hidden">ทั้งหมด</span>
              <span className="hidden sm:inline">ทุกฤดูกาล</span>
            </span>
            <span className={chipCount(selectedSeason === 'ALL')}>{countBySeason('ALL')}</span>
          </button>
          {seasonsOrder.map((seasonKey) => {
            const cfg = SEASON_CONFIGS[seasonKey];
            const active = selectedSeason === seasonKey;
            return (
              <button
                key={seasonKey}
                type="button"
                onClick={() => onSelectSeason(seasonKey)}
                title={`${cfg.nameTh} · ${cfg.monthsRangeTh}`}
                className={chip(active)}
              >
                <span className="flex items-center gap-0.5 sm:gap-1.5 whitespace-nowrap max-w-full">
                  <span className="text-[11px] sm:text-sm leading-none">{cfg.icon}</span>
                  <span className="truncate">{cfg.nameTh.replace('ฤดู', '')}</span>
                </span>
                <span className={chipCount(active)}>{countBySeason(seasonKey)}</span>
              </button>
            );
          })}
        </div>

        {/* Status · year · audio: one row on phones, pills from sm up */}
        <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-1.5 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
          <FilterSelect<AiringFilter>
            value={airingFilter}
            onChange={onSelectAiringFilter}
            ariaLabel="สถานะการฉาย"
            title="เรื่องที่เพิ่งจบจะอยู่กับกำลังฉายอีก 3 สัปดาห์ แล้วค่อยย้ายไปจบแล้ว"
            options={(Object.keys(AIRING_FILTER_LABELS) as AiringFilter[]).map((key) => ({
              value: key,
              label: AIRING_FILTER_LABELS[key],
              count: countWith({ airing: key }),
            }))}
          />

          <FilterSelect<string>
            value={String(selectedYear)}
            onChange={(v) => onSelectYear(v === 'ALL' ? 'ALL' : parseInt(v, 10))}
            ariaLabel="เลือกปีที่ฉาย"
            options={[
              { value: 'ALL', label: 'ทุกปี', count: countWith({ year: 'ALL' }) },
              ...availableYears.map((y) => ({ value: String(y), label: String(y), count: countWith({ year: y }) })),
            ]}
          />

          {/* Audio: a dropdown on phones, the segmented toggle from sm up */}
          <FilterSelect<'ALL' | 'ซับไทย' | 'พากย์ไทย'>
            className="sm:hidden"
            align="right"
            value={selectedAudio}
            onChange={onSelectAudio}
            ariaLabel="เลือกเสียง"
            highlight={selectedAudio !== 'ALL'}
            options={(['ALL', 'ซับไทย', 'พากย์ไทย'] as const).map((a) => ({
              value: a,
              label: a === 'ALL' ? 'ทุกเสียง' : a,
              count: countWith({ audio: a }),
            }))}
          />
          <div className="hidden sm:flex items-center h-9 p-1 rounded-full bg-white/[0.03] border border-white/[0.07]">
            {(['ALL', 'ซับไทย', 'พากย์ไทย'] as const).map((aud) => (
              <button
                key={aud}
                type="button"
                onClick={() => onSelectAudio(aud)}
                className={`h-full px-3 rounded-full text-[12px] font-medium transition-all ${
                  selectedAudio === aud
                    ? 'bg-fuchsia-500/90 text-white shadow'
                    : 'text-slate-400 hover:text-slate-100'
                }`}
              >
                {aud === 'ALL' ? 'ทุกเสียง' : aud}
                <span className="ml-1 opacity-60">{countWith({ audio: aud })}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {filteredList.length === 0 && !isDefault && (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/[0.06] px-3 py-2">
          <span className="text-[12px] text-slate-300">ไม่มีเรื่องที่ตรงกับตัวกรองทั้งหมดนี้</span>
          <button
            type="button"
            onClick={onResetFilters}
            className="h-7 px-3 rounded-full bg-fuchsia-500/90 hover:bg-fuchsia-500 text-white text-[12px] font-semibold flex items-center gap-1 shrink-0"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>รีเซ็ตตัวกรอง</span>
          </button>
        </div>
      )}

      {/* Quick stats — phones: one row of slim pills; sm+: cards */}
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
        {stats.map(({ label, short, value, icon: Icon, tone, onClick }) => (
          <button
            key={label}
            type="button"
            onClick={onClick}
            disabled={!onClick}
            title={label}
            className="glass-panel min-w-0 rounded-xl sm:rounded-2xl h-9 sm:h-auto px-1.5 sm:px-3.5 sm:py-3 flex items-center justify-center sm:justify-start gap-1 sm:gap-3 text-left enabled:hover:bg-white/[0.05] transition-colors disabled:cursor-default"
          >
            <span className={`w-5 h-5 sm:w-9 sm:h-9 rounded-md sm:rounded-xl grid place-items-center shrink-0 ${tone}`}>
              <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
            </span>
            <span className="min-w-0 flex items-baseline gap-1 sm:block">
              <span className="text-[13px] sm:text-lg font-bold sm:font-semibold text-white leading-none">
                {value}
              </span>
              <span className="truncate text-[10px] sm:text-xs text-slate-500 sm:mt-1 leading-tight sm:block">
                <span className="sm:hidden">{short}</span>
                <span className="hidden sm:inline">{label}</span>
              </span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};
